import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../ui/Card'
import { Button } from '../ui/Button'
import { StripeProduct, formatPrice } from '../../stripe-config'
import { supabase } from '../../lib/supabase'
import { Alert, AlertDescription } from '../ui/Alert'

interface PricingCardProps {
  product: StripeProduct
  isAuthenticated: boolean
  onLoginRequired: () => void
}

export function PricingCard({ product, isAuthenticated, onLoginRequired }: PricingCardProps) {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const navigate = useNavigate()

  const handlePurchase = async () => {
    if (product.isEnterprise) {
      navigate('/contact')
      return
    }

    if (!isAuthenticated) {
      navigate(`/signup?plan=${product.priceId}`)
      return
    }

    setLoading(true)
    setError(null)

    try {
      const { data: { session } } = await supabase.auth.getSession()

      if (!session) {
        onLoginRequired()
        return
      }

      if (product.priceId === 'price_free_trial') {
        const { data: profile } = await supabase
          .from('user_profiles')
          .select('account_type, has_used_trial')
          .eq('user_id', session.user.id)
          .single()

        if (profile?.has_used_trial) {
          setError('You have already used your free trial. Please choose a paid plan.')
          return
        }

        if (profile?.account_type === 'free_trial') {
          navigate('/generator')
          return
        }

        const { error: profileError } = await supabase
          .from('user_profiles')
          .update({ account_type: 'free_trial', has_used_trial: false })
          .eq('user_id', session.user.id)

        if (profileError) {
          throw new Error('Failed to activate free trial')
        }

        navigate('/generator')
        return
      }

      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/stripe-checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          price_id: product.priceId,
          mode: product.mode,
          success_url: `${window.location.origin}/success?session_id={CHECKOUT_SESSION_ID}`,
          cancel_url: `${window.location.origin}/pricing`,
        }),
      })

      if (!response.ok) {
        const data = await response.json()
        throw new Error(data.error || `Checkout failed with status ${response.status}`)
      }

      const data = await response.json()

      if (data.url) {
        window.location.href = data.url
      } else {
        throw new Error('No checkout URL received')
      }
    } catch (err) {
      console.error('Checkout error:', err)
      const errorMessage = err instanceof Error ? err.message : 'Failed to start checkout process. Please try again.'
      setError(errorMessage)
      alert(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  const isPopular = product.isPopular || false

  return (
    <Card className={`w-full h-full flex flex-col ${isPopular ? 'border-2 border-[#00A884] shadow-2xl bg-gradient-to-br from-emerald-50 to-white scale-105 relative z-10' : 'border border-gray-200 shadow-md'}`}>
      <CardHeader>
        {isPopular && (
          <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 w-full flex justify-center">
            <span className="inline-block px-6 py-2 text-sm font-accent lowercase text-white bg-gradient-to-r from-[#00A884] to-[#00C49A] rounded-full shadow-lg">
              most popular
            </span>
          </div>
        )}
        <CardTitle className={`text-2xl font-header font-bold ${isPopular ? 'text-emerald-900 mt-2' : ''}`}>{product.name}</CardTitle>
        <CardDescription className={isPopular ? 'text-gray-700' : ''}>{product.description}</CardDescription>
        {product.disclaimer && (
          <div className="text-xs text-gray-500 italic mt-2">
            {product.disclaimer}
          </div>
        )}
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="text-3xl font-header font-bold">
          {formatPrice(product.price, product.currency)}
          {product.mode === 'subscription' && product.price !== 'custom' && <span className="text-lg font-body font-normal text-gray-600">/month</span>}
        </div>

        <ul className="space-y-2 pt-4">
          {product.features.map((feature, index) => (
            <li key={index} className="flex items-start">
              <svg className="w-5 h-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
              </svg>
              <span className="text-sm text-gray-700">{feature}</span>
            </li>
          ))}
        </ul>

        {error && (
          <Alert variant="destructive" className="mt-4">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
      </CardContent>
      <CardFooter className="mt-auto">
        <Button
          onClick={handlePurchase}
          disabled={loading}
          className={`w-full font-header ${isPopular ? 'bg-gradient-to-r from-[#00A884] to-[#00C49A] hover:from-[#00C49A] hover:to-[#00E5B8] shadow-lg text-lg font-semibold' : ''} ${product.isEnterprise ? 'bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 shadow-lg text-lg font-semibold' : ''}`}
          data-price-id={product.priceId}
        >
          {loading ? 'Processing...' : product.isEnterprise ? 'Contact Us' : 'Get Started'}
        </Button>
      </CardFooter>
    </Card>
  )
}